import React, { useCallback, useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { Topbar } from '@/components/shared/Topbar'
import {
  loadGoogleMaps,
  initMap,
  placeMarkers,
  drawRoute,
  fitBounds,
  resolveStopCoordinates,
} from '@/services/maps'
import { openDeal, NETWORK_LABELS } from '@/services/affiliates'
import { DESTINATIONS } from '@/data/destinations'
import { fetchImages, type Img } from '@/services/images'
import type { Stop, Destination } from '@/types'

// ─── Fallback canvas map — used when no Maps API key is configured or the
// Maps JS API fails to load. Dark forest-green / candlelight-gold, matching
// Sanctum's sacred palette exactly, so the fallback never breaks the mood.
// Markers drawn as teardrop pins, matching the real-map marker treatment. ──

function drawTeardropPin(ctx: CanvasRenderingContext2D, x: number, y: number, num: number) {
  const scale = 0.9
  ctx.save()
  ctx.translate(x, y)
  ctx.scale(scale, scale)
  // Pin silhouette: rounded head tapering to a point below.
  ctx.beginPath()
  ctx.moveTo(0, 18)
  ctx.bezierCurveTo(6, 8, 14, -1, 14, -10)
  ctx.bezierCurveTo(14, -18, 8, -24, 0, -24)
  ctx.bezierCurveTo(-8, -24, -14, -18, -14, -10)
  ctx.bezierCurveTo(-14, -1, -6, 8, 0, 18)
  ctx.closePath()
  ctx.shadowColor = 'rgba(201,151,58,0.5)'
  ctx.shadowBlur = 8
  ctx.fillStyle = '#C9973A'
  ctx.fill()
  ctx.shadowBlur = 0
  ctx.lineWidth = 1.5
  ctx.strokeStyle = '#F8F5EF'
  ctx.stroke()
  ctx.fillStyle = '#081C10'
  ctx.font = 'bold 11px Cinzel, Georgia, serif'
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
  ctx.fillText(String(num), 0, -10)
  ctx.restore()
}

function FallbackMap({ stopCount }: { stopCount: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const W = canvas.width, H = canvas.height
    ctx.clearRect(0, 0, W, H)

    const bg = ctx.createLinearGradient(0, 0, W, H)
    bg.addColorStop(0, '#0E2717'); bg.addColorStop(1, '#050E08')
    ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H)

    ctx.strokeStyle = 'rgba(201,151,58,0.07)'; ctx.lineWidth = 0.8
    for (let x = 0; x < W; x += 38) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke() }
    for (let y = 0; y < H; y += 38) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke() }

    const pts = [[55, 185], [135, 118], [215, 82], [300, 132], [380, 178]].slice(0, Math.max(stopCount, 1))

    // Dashed candlelight-gold route
    ctx.beginPath(); ctx.strokeStyle = 'rgba(201,151,58,0.65)'; ctx.lineWidth = 2; ctx.setLineDash([6, 5])
    pts.forEach(([x, y], i) => i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y))
    ctx.stroke(); ctx.setLineDash([])

    ctx.fillStyle = 'rgba(232,196,122,0.55)'
    pts.slice(0, -1).forEach(([x1, y1], i) => {
      const [x2, y2] = pts[i + 1]
      ctx.beginPath(); ctx.arc((x1 + x2) / 2, (y1 + y2) / 2, 2.5, 0, Math.PI * 2); ctx.fill()
    })

    pts.forEach(([x, y], i) => drawTeardropPin(ctx, x, y, i + 1))

    ctx.fillStyle = 'rgba(248,245,239,0.3)'
    ctx.font = '9px "EB Garamond", Georgia, serif'
    ctx.textAlign = 'center'
    ctx.fillText('A quiet map, drawn while the sacred one loads', W / 2, H - 10)
  }, [stopCount])

  return <canvas ref={canvasRef} width={430} height={250} style={{ width: '100%', height: '100%', display: 'block' }} />
}

// ─── Real Google Maps view ─────────────────────────────────────────────────────

function GoogleMapView({ stops, context, destination, onStopClick }: { stops: Stop[]; context: string; destination: Destination; onStopClick?: (i: number) => void }) {
  const mapDivRef = useRef<HTMLDivElement>(null)
  const markersRef = useRef<google.maps.Marker[]>([])
  const [mapError, setMapError] = useState<string | null>(null)
  const [mapsLoaded, setMapsLoaded] = useState(false)

  const buildMap = useCallback(async () => {
    if (!mapDivRef.current || stops.length === 0) return
    try {
      const stopNames = stops.map((s) => s.name)

      // Reuse the same lazily-fetched thumbnails the sidebar shows, so the
      // InfoWindow popup and sidebar stay visually consistent — one image
      // fetch per stop, cached by the images service.
      const extras = await Promise.all(
        stops.map(async (s) => {
          const imgs = await fetchImages(`${s.name} ${destination.name}`, 1)
          return { imageUrl: imgs[0]?.thumb || imgs[0]?.url, timeLabel: s.time }
        })
      )

      const resolvedStops = await resolveStopCoordinates(stopNames, context, extras)
      if (resolvedStops.length === 0) { setMapError('Could not locate stops on map'); return }

      const centre = resolvedStops[0].position
      const map = await initMap(mapDivRef.current!, centre, 8)

      const markers = await placeMarkers(map, resolvedStops)
      markersRef.current = markers
      markers.forEach((marker, i) => marker.addListener('click', () => onStopClick?.(i)))

      await drawRoute(map, resolvedStops)
      await fitBounds(map, resolvedStops)

      setMapsLoaded(true)
    } catch (err) {
      console.warn('Sanctum map render error:', err)
      setMapError(String(err))
    }
  }, [stops, onStopClick, destination])

  useEffect(() => {
    loadGoogleMaps().then(() => buildMap()).catch((err) => setMapError(String(err)))
    return () => { markersRef.current.forEach((m) => m.setMap(null)); markersRef.current = [] }
  }, [buildMap])

  if (mapError) return <FallbackMap stopCount={stops.length} />

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div ref={mapDivRef} style={{ width: '100%', height: '100%', minHeight: 220 }} />
      {!mapsLoaded && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--void)' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {[0, 1, 2].map((i) => (
              <motion.div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--candle)' }}
                animate={{ y: [0, -7, 0], opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.25 }} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Stop photo thumbnail — lazily fetched, cached by the images service;
// skips gracefully (renders nothing extra) if no image resolves. Never
// blocks map render — fetched independently per stop on mount. ────────────

function StopThumb({ stop, destination }: { stop: Stop; destination: Destination }) {
  const [img, setImg] = useState<Img | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchImages(`${stop.name} ${destination.name}`, 1).then((imgs) => {
      if (!cancelled && imgs[0]) setImg(imgs[0])
    })
    return () => { cancelled = true }
  }, [stop.name, destination.name])

  if (!img) {
    return (
      <div style={{ width: 44, height: 44, flexShrink: 0, background: 'var(--forest3)', border: '1px solid var(--hairline)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
        {destination.emoji}
      </div>
    )
  }

  return (
    <div style={{ width: 44, height: 44, flexShrink: 0, overflow: 'hidden', border: '1px solid var(--hairline2)', boxShadow: '0 2px 10px rgba(0,0,0,0.4)' }}>
      <img
        src={img.thumb || img.url}
        alt={img.alt || stop.name}
        loading="lazy"
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
        style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'saturate(0.8) brightness(0.82)' }}
      />
    </div>
  )
}

export function JourneyScreen() {
  const { selectedDestination, dnaKey, setScreen, hasUnlocked } = useAppStore()
  const [activeStop, setActiveStop] = useState(0)
  const dest = selectedDestination ?? (DESTINATIONS[dnaKey]?.[0] || null)
  if (!dest) { setScreen('dna'); return null }

  // Returning users who already unlocked via the email gate skip straight
  // to the journey card — the gate only ever shows once per visitor.
  const goUnlock = () => setScreen(hasUnlocked ? 'card' : 'gate')

  return (
    <div style={{ minHeight: '100vh', background: 'var(--void)', display: 'flex', flexDirection: 'column' }}>
      <Topbar center={dest.name} backTo="dna" />

      {/* Header */}
      <div style={{ background: 'var(--forest)', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--hairline)' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--serif-caps)', fontSize: 18, color: 'var(--ivory)', letterSpacing: '0.06em' }}>{dest.name}</h2>
          <p style={{ fontFamily: 'var(--serif-body)', fontSize: 12, color: 'var(--candle)', marginTop: 2, fontStyle: 'italic' }}>
            {dest.stops.length} sacred stops · {dest.faith.toLowerCase()}
          </p>
        </div>
        <div style={{ background: 'var(--candle)', color: 'var(--forest)', fontFamily: 'var(--serif-caps)', fontSize: 10, padding: '5px 13px', letterSpacing: '0.06em', boxShadow: '0 0 16px rgba(201,151,58,0.35)' }}>
          Save {dest.saving}
        </div>
      </div>

      {/* Map + stops */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', minHeight: 250, borderBottom: '1px solid var(--hairline)' }}>
        <div style={{ background: 'var(--void)', borderRight: '1px solid var(--hairline)', minHeight: 220 }}>
          <GoogleMapView context={dest.name} stops={dest.stops} destination={dest} onStopClick={setActiveStop} />
        </div>
        <div style={{ overflowY: 'auto', background: 'var(--forest)' }}>
          <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, letterSpacing: '0.14em', color: 'var(--ivory-faint)', textTransform: 'uppercase', padding: '10px 12px 6px' }}>
            Your Curated Stops
          </div>
          {dest.stops.map((stop: Stop, i: number) => (
            <button key={i} onClick={() => setActiveStop(i)} style={{
              width: '100%', textAlign: 'left', padding: '10px 12px',
              borderBottom: '1px solid var(--hairline)', cursor: 'pointer',
              display: 'flex', gap: 9, alignItems: 'flex-start',
              background: activeStop === i ? 'rgba(201,151,58,0.08)' : 'transparent',
              borderLeft: activeStop === i ? '2px solid var(--candle)' : '2px solid transparent',
            }}>
              <StopThumb stop={stop} destination={dest} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
                  <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'var(--candle)', color: 'var(--forest)', fontFamily: 'var(--serif-caps)', fontSize: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</div>
                  <span style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, letterSpacing: '0.1em', color: 'var(--candle2)', textTransform: 'uppercase' }}>{stop.time}</span>
                </div>
                <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 11, color: 'var(--ivory)', letterSpacing: '0.03em' }}>{stop.name}</div>
                <div style={{ fontFamily: 'var(--serif-body)', fontSize: 10, color: 'var(--ivory-faint)', marginTop: 2, lineHeight: 1.4, fontStyle: 'italic' }}>{stop.description.slice(0, 50)}…</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Active stop detail */}
      {dest.stops[activeStop] && (
        <motion.div key={activeStop} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          style={{ margin: '0 18px', padding: '14px 0', borderBottom: '1px solid var(--hairline)' }}>
          <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.1em', color: 'var(--candle)', textTransform: 'uppercase', marginBottom: 4 }}>{dest.stops[activeStop].time}</div>
          <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 15, color: 'var(--ivory)', marginBottom: 6, letterSpacing: '0.03em' }}>{dest.stops[activeStop].name}</div>
          <div style={{ fontFamily: 'var(--serif-body)', fontSize: 14, color: 'var(--ivory-dim)', lineHeight: 1.8, fontStyle: 'italic' }}>{dest.stops[activeStop].description}</div>
          <div style={{ marginTop: 8, fontFamily: 'var(--serif-caps)', fontSize: 9, color: 'var(--candle2)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{dest.stops[activeStop].spiritualNote}</div>
        </motion.div>
      )}

      {/* Deals */}
      <div style={{ padding: '12px 18px 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.12em', color: 'var(--ivory-faint)', textTransform: 'uppercase' }}>◈ Pilgrim partner offers — respectfully curated</span>
        <span style={{ background: 'var(--forest3)', color: 'var(--candle2)', fontFamily: 'var(--serif-caps)', fontSize: 8, padding: '3px 10px', border: '1px solid var(--hairline)' }}>Live rates</span>
      </div>
      <div style={{ marginBottom: 8 }}>
        {dest.deals.map((deal, i) => (
          <motion.button key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }}
            onClick={() => openDeal(deal, dest.name)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '10px 18px', background: 'transparent', border: 'none', borderBottom: '1px solid var(--hairline)', cursor: 'pointer', textAlign: 'left' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(201,151,58,0.06)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <div style={{ width: 34, height: 34, background: 'var(--forest3)', border: '1px solid var(--hairline)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{deal.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 11, color: 'var(--ivory)', letterSpacing: '0.03em' }}>{deal.title}</div>
              <div style={{ fontFamily: 'var(--serif-body)', fontSize: 11, color: 'var(--ivory-faint)', marginTop: 1, fontStyle: 'italic' }}>{deal.detail}</div>
              <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, color: 'var(--sage)', letterSpacing: '0.06em', marginTop: 2, textTransform: 'uppercase' }}>via {NETWORK_LABELS[deal.network] || deal.network}</div>
            </div>
            <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, padding: '3px 8px', border: '1px solid var(--hairline2)', color: 'var(--candle2)', whiteSpace: 'nowrap' }}>{deal.saving}</div>
          </motion.button>
        ))}
      </div>

      {/* CTA */}
      <div style={{ padding: '12px 14px', background: 'var(--forest)', display: 'grid', gridTemplateColumns: '1fr 1.8fr', gap: 8, marginTop: 'auto', borderTop: '1px solid var(--hairline)' }}>
        <button onClick={() => setScreen('dna')} style={{ background: 'rgba(248,245,239,0.06)', border: '1px solid var(--hairline2)', color: 'var(--candle2)', fontFamily: 'var(--serif-caps)', fontSize: 10, letterSpacing: '0.1em', padding: 11, cursor: 'pointer', textTransform: 'uppercase' }}>← Back</button>
        <motion.button onClick={goUnlock} whileTap={{ scale: 0.98 }}
          style={{ background: 'var(--candle)', border: 'none', color: 'var(--forest)', fontFamily: 'var(--serif-caps)', fontSize: 11, letterSpacing: '0.1em', padding: 11, cursor: 'pointer', textTransform: 'uppercase', boxShadow: '0 0 22px rgba(201,151,58,0.4)' }}>
          Unlock my full pilgrimage →
        </motion.button>
      </div>
    </div>
  )
}
